import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Columns3, GripVertical, ChevronRight } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  getMetadataColumnsForItemType,
  ColumnOption 
} from "@/lib/dataSources/shared/tableColumns";
import { useZoom } from "@/providers/ZoomProvider";

// Section interface for grouping columns
interface Section {
  id: string;
  title: string;
  columns: ColumnOption[];
}

// Props for sortable column item
interface SortableColumnItemProps {
  id: string;
  label: string;
  isSelected: boolean;
  onToggle: () => void;
}

// Sortable column item component with visibility checkbox
function SortableColumnItem({ id, label, isSelected, onToggle }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isSelected });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-1.5 rounded text-sm ${
        isSelected ? "bg-muted/50" : "opacity-60"
      } ${isDragging ? "shadow-lg z-50 border border-primary" : ""}`}
    >
      {isSelected && (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded"
          type="button"
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </button>
      )}
      {!isSelected && <div className="w-4" />}
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        className="h-4 w-4"
      />
      <span className={`truncate flex-1 ${!isSelected ? "text-muted-foreground" : ""}`}>
        {label}
      </span>
    </div>
  );
}

// Props for sortable section item
interface SortableSectionProps {
  section: Section;
  selectedColumns: string[];
  onColumnToggle: (columnId: string) => void;
  onColumnsReorder: (sectionId: string, newColumnOrder: string[]) => void;
  onSectionToggle: (sectionId: string, selectAll: boolean) => void;
  zoom: number;
}

// Sortable section component with nested column sorting and visibility
function SortableSection({ 
  section, 
  selectedColumns, 
  onColumnToggle,
  onColumnsReorder,
  onSectionToggle,
  zoom,
}: SortableSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get all columns in this section
  const sectionColumnIds = section.columns.map(col => col.id);
  
  // Get selected columns in order
  const selectedSectionColumns = selectedColumns.filter(id => sectionColumnIds.includes(id));
  
  // Get unselected columns
  const unselectedSectionColumns = sectionColumnIds.filter(id => !selectedColumns.includes(id));
  
  // All columns to display: selected first (in order), then unselected
  const displayColumns = [...selectedSectionColumns, ...unselectedSectionColumns];

  // Check if all columns in section are selected
  const allSelected = sectionColumnIds.every(id => selectedColumns.includes(id));
  const someSelected = sectionColumnIds.some(id => selectedColumns.includes(id));

  // DnD sensors for column reordering within section
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Zoom modifier to adjust drag coordinates for platform zoom
  const zoomModifier: Modifier = useMemo(() => {
    return ({ transform }) => ({
      ...transform,
      x: transform.x / zoom,
      y: transform.y / zoom,
    });
  }, [zoom]);

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedSectionColumns.indexOf(active.id as string);
      const newIndex = selectedSectionColumns.indexOf(over.id as string);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(selectedSectionColumns, oldIndex, newIndex);
        onColumnsReorder(section.id, newOrder);
      }
    }
  };

  const getColumnLabel = (columnId: string): string => {
    const col = section.columns.find(c => c.id === columnId);
    return col?.label || columnId;
  };

  const hasSelectedColumns = selectedSectionColumns.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded border border-border bg-background p-3 ${
        isDragging ? "shadow-lg z-50" : ""
      }`}
    >
      {/* Section header with drag handle, collapse toggle, and select all */}
      <div className={`flex items-center gap-2 ${!isCollapsed ? "mb-2 pb-2 border-b border-border" : ""}`}>
        {hasSelectedColumns && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
            type="button"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {!hasSelectedColumns && <div className="w-6" />}
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-0.5 hover:bg-muted rounded"
        >
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              !isCollapsed ? "rotate-90" : ""
            }`}
          />
        </button>
        <Checkbox
          checked={allSelected}
          onCheckedChange={() => onSectionToggle(section.id, !allSelected)}
          className={`h-4 w-4 ${someSelected && !allSelected ? 'data-[state=unchecked]:bg-muted-foreground/50' : ''}`}
        />
        <span className="font-medium text-sm flex-1">{section.title}</span>
        <span className="text-xs text-muted-foreground">
          {selectedSectionColumns.length}/{sectionColumnIds.length}
        </span>
      </div>

      {/* Columns within section - collapsible */}
      {!isCollapsed && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleColumnDragEnd}
          modifiers={[zoomModifier]}
        >
          <SortableContext
            items={selectedSectionColumns}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1 pl-6">
              {displayColumns.map((columnId) => (
                <SortableColumnItem
                  key={columnId}
                  id={columnId}
                  label={getColumnLabel(columnId)}
                  isSelected={selectedColumns.includes(columnId)}
                  onToggle={() => onColumnToggle(columnId)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// Props for the main dialog
interface ColumnOrderDialogProps {
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  itemType?: string;
  sections?: Array<{
    id: string;
    title: string;
    fields: Array<{ id: string; label: string; type: string }>;
  }>;
}

export function ColumnOrderDialog({
  selectedColumns,
  onColumnsChange,
  itemType = "library",
  sections: templateSections = [],
}: ColumnOrderDialogProps) {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Get platform zoom to adjust drag-and-drop coordinates
  const { zoom } = useZoom();

  // Get metadata columns for this item type
  const metadataColumns = useMemo(() => getMetadataColumnsForItemType(itemType), [itemType]);

  // Build sections array: Metadata + template sections
  const allSections = useMemo((): Section[] => {
    const result: Section[] = [];

    // Add Metadata section
    result.push({
      id: '_metadata',
      title: 'Metadata',
      columns: metadataColumns,
    });

    // Add template sections if available
    templateSections.forEach(section => {
      result.push({
        id: section.id,
        title: section.title,
        columns: section.fields.map(field => ({
          id: `field_${field.id}`,
          label: field.label,
          type: 'field' as const,
          sectionId: section.id,
        })),
      });
    });

    return result;
  }, [metadataColumns, templateSections]);

  // Get sections ordered by first occurrence of their selected columns
  const orderedSections = useMemo(() => {
    // Build a map of section order based on first occurrence of their columns in selectedColumns
    const sectionFirstIndex: Record<string, number> = {};
    
    allSections.forEach(section => {
      const sectionColumnIds = section.columns.map(col => col.id);
      let minIndex = Infinity;
      
      selectedColumns.forEach((colId, index) => {
        if (sectionColumnIds.includes(colId)) {
          minIndex = Math.min(minIndex, index);
        }
      });
      
      sectionFirstIndex[section.id] = minIndex;
    });

    // Sort sections: those with selected columns first (by order), then others
    return [...allSections].sort((a, b) => {
      const aIndex = sectionFirstIndex[a.id];
      const bIndex = sectionFirstIndex[b.id];
      
      // Both have selected columns - sort by first occurrence
      if (aIndex !== Infinity && bIndex !== Infinity) {
        return aIndex - bIndex;
      }
      // Only a has selected columns - a comes first
      if (aIndex !== Infinity) return -1;
      // Only b has selected columns - b comes first
      if (bIndex !== Infinity) return 1;
      // Neither has selected columns - keep original order
      return 0;
    });
  }, [allSections, selectedColumns]);

  // Sections with at least one selected column (for drag-and-drop)
  const sectionsWithSelectedColumns = useMemo(() => {
    return orderedSections.filter(section => {
      const sectionColumnIds = section.columns.map(col => col.id);
      return sectionColumnIds.some(id => selectedColumns.includes(id));
    });
  }, [orderedSections, selectedColumns]);

  // DnD sensors for section reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Zoom modifier to adjust drag coordinates for platform zoom
  const zoomModifier: Modifier = useMemo(() => {
    return ({ transform }) => ({
      ...transform,
      x: transform.x / zoom,
      y: transform.y / zoom,
    });
  }, [zoom]);

  // Handle section drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveSection(event.active.id as string);
  };

  // Handle section drag end - reorder entire sections
  const handleSectionDragEnd = (event: DragEndEvent) => {
    setActiveSection(null);
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = sectionsWithSelectedColumns.findIndex(s => s.id === active.id);
    const newIndex = sectionsWithSelectedColumns.findIndex(s => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder sections
    const newSectionOrder = arrayMove(sectionsWithSelectedColumns, oldIndex, newIndex);

    // Rebuild selectedColumns based on new section order
    const newSelectedColumns: string[] = [];
    newSectionOrder.forEach(section => {
      const sectionColumnIds = section.columns.map(col => col.id);
      // Get selected columns from this section, preserving their internal order
      const sectionSelected = selectedColumns.filter(id => sectionColumnIds.includes(id));
      newSelectedColumns.push(...sectionSelected);
    });

    onColumnsChange(newSelectedColumns);
  };

  // Handle column visibility toggle
  const handleColumnToggle = (columnId: string) => {
    if (selectedColumns.includes(columnId)) {
      // Remove column
      onColumnsChange(selectedColumns.filter(id => id !== columnId));
    } else {
      // Add column - find the right position based on section order
      const columnSection = allSections.find(s => 
        s.columns.some(c => c.id === columnId)
      );
      
      if (!columnSection) {
        onColumnsChange([...selectedColumns, columnId]);
        return;
      }

      // Find where this section's columns are in the current selection
      const sectionColumnIds = columnSection.columns.map(c => c.id);
      const lastSectionColumnIndex = selectedColumns.reduce((lastIdx, col, idx) => {
        if (sectionColumnIds.includes(col)) return idx;
        return lastIdx;
      }, -1);

      if (lastSectionColumnIndex === -1) {
        // No columns from this section yet - add at end
        onColumnsChange([...selectedColumns, columnId]);
      } else {
        // Insert after the last column from this section
        const newColumns = [...selectedColumns];
        newColumns.splice(lastSectionColumnIndex + 1, 0, columnId);
        onColumnsChange(newColumns);
      }
    }
  };

  // Handle section toggle (select all / deselect all)
  const handleSectionToggle = (sectionId: string, selectAll: boolean) => {
    const section = allSections.find(s => s.id === sectionId);
    if (!section) return;

    const sectionColumnIds = section.columns.map(c => c.id);

    if (selectAll) {
      // Add all columns from this section that aren't already selected
      const columnsToAdd = sectionColumnIds.filter(id => !selectedColumns.includes(id));
      
      // Find where to insert them
      const lastSectionColumnIndex = selectedColumns.reduce((lastIdx, col, idx) => {
        if (sectionColumnIds.includes(col)) return idx;
        return lastIdx;
      }, -1);

      if (lastSectionColumnIndex === -1) {
        // No columns from this section yet - add at end
        onColumnsChange([...selectedColumns, ...columnsToAdd]);
      } else {
        // Insert after the last column from this section
        const newColumns = [...selectedColumns];
        newColumns.splice(lastSectionColumnIndex + 1, 0, ...columnsToAdd);
        onColumnsChange(newColumns);
      }
    } else {
      // Remove all columns from this section
      onColumnsChange(selectedColumns.filter(id => !sectionColumnIds.includes(id)));
    }
  };

  // Handle column reorder within a section
  const handleColumnsReorder = (sectionId: string, newColumnOrder: string[]) => {
    const section = allSections.find(s => s.id === sectionId);
    if (!section) return;

    const sectionColumnIds = new Set(section.columns.map(col => col.id));

    // Rebuild selectedColumns:
    // 1. Keep columns from other sections in place
    // 2. Replace this section's columns with the new order
    const newSelectedColumns: string[] = [];
    let sectionColumnsInserted = false;

    selectedColumns.forEach(colId => {
      if (sectionColumnIds.has(colId)) {
        // This column belongs to the reordered section
        if (!sectionColumnsInserted) {
          // Insert all reordered columns at the first occurrence
          newSelectedColumns.push(...newColumnOrder);
          sectionColumnsInserted = true;
        }
        // Skip individual columns from this section (already added in bulk)
      } else {
        // Column from another section, keep in place
        newSelectedColumns.push(colId);
      }
    });

    onColumnsChange(newSelectedColumns);
  };

  const activeSectionData = activeSection 
    ? sectionsWithSelectedColumns.find(s => s.id === activeSection)
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 gap-1 text-xs border-border"
        >
          <Columns3 className="h-3 w-3" />
          <span>Columns</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Columns</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            Toggle column visibility and drag to reorder. Sections can also be reordered.
          </p>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleSectionDragEnd}
            modifiers={[zoomModifier]}
          >
            <SortableContext
              items={sectionsWithSelectedColumns.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {orderedSections.map((section) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    selectedColumns={selectedColumns}
                    onColumnToggle={handleColumnToggle}
                    onColumnsReorder={handleColumnsReorder}
                    onSectionToggle={handleSectionToggle}
                    zoom={zoom}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeSectionData ? (
                <div className="rounded border border-primary bg-background p-3 shadow-lg">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{activeSectionData.title}</span>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ColumnOrderDialog;
